/**
 * What one send from the content chat carries — its text, the staged moment
 * (never on a reply), the staged files and the reply target — and the draft
 * the sender uploads from it.
 *
 * The sender keys its retry state (the client id, and every upload already
 * finished) on the draft object. So one attempt builds its draft once and
 * every retry reuses it, until the send succeeds or its text or what's
 * staged changes: the server answers a reused client id with the message it
 * already has, so an edited resend must be a new attempt. A new attempt still
 * uploads nothing the server already holds: the session's upload ledger
 * remembers what each staged file and frame uploaded as, and every draft
 * marks those items as the server's copies.
 * Building compresses the images; every other file rides as picked.
 */
import {
  FILE_TOO_LARGE_CODE,
  FRAME_ITEM_INDEX,
  MessageSendError,
  type EncodedImage,
  type FileDraft,
  type MessageDraft,
  type SendCallbacks,
  type UploadedFile,
} from '@discovr/features';

import {
  COMPRESSIBLE_KIND,
  compressImageFile,
  ImageTooLargeError,
  IMAGE_TOO_LARGE_CODE,
  ENCODED_IMAGE_EXTENSION,
  pickSmaller,
  type CompressedImage,
} from '../../../../../lib/media/compressImage';
import type { StagedFile } from '../../../../../lib/media/pickFiles';
import type { StagedFileState } from '../../../ChatThread';

const EXTENSION_SEPARATOR = '.';
const NO_EXTENSION = -1;
const EMPTY_RING = 0;
const DEFAULT_REFUSAL_MESSAGE = 'the server refused the file';
/** A file an earlier send already uploaded shows a full ring. */
const FULL_RING = 1;
/**
 * Chip progress moves in whole percents — the finest step its ring shows — so
 * the many progress events an upload fires repaint a chip only when its ring
 * would change.
 */
const PROGRESS_STEPS = 100;

/** A staged moment's payload: a timestamp, and a captured frame when there is one. */
export type MomentDraft = NonNullable<MessageDraft['moment']>;

/**
 * The moment waiting in the composer, and the local preview of its frame.
 * Replaced, never mutated, whenever the moment changes — so its identity
 * tells a retry from a new send.
 */
export interface StagedMomentState {
  moment: MomentDraft;
  frameUrl?: string;
}

/** What a send carries. */
export interface SendContents {
  /** The message text, as sent. */
  body: string;
  /** Null on a reply, which uses the original message's moment. */
  staged: StagedMomentState | null;
  files: StagedFile[];
  replyTo?: string;
}

export interface SendAttempt extends SendContents {
  /** The draft, once building has started; dropped again if building fails. */
  draft?: Promise<MessageDraft>;
}

export function newSendAttempt(contents: SendContents): SendAttempt {
  return { ...contents };
}

/**
 * What the server already holds from this session's sends. An upload no
 * message claims stays pending on the server, counting against the user's
 * upload quota, until a message sends it or it expires — so a later send
 * names these copies rather than uploading the same item again.
 */
export interface UploadLedger {
  /** Staged file id → the server's file id. Only uploads that came back with an id, never bytes sent alone. */
  files: Map<string, string>;
  /**
   * Staged moment → its frame's upload. Keyed by the moment object, which is
   * replaced whenever the moment changes, so a dropped or replaced moment
   * takes its entry with it.
   */
  frames: WeakMap<StagedMomentState, UploadedFile>;
}

export function newUploadLedger(): UploadLedger {
  return { files: new Map(), frames: new WeakMap() };
}

/**
 * Forgets every file the ledger holds except those a send in flight carries,
 * for a session leaving its entry: those server ids belong to the old entry.
 * Returns the forgotten server ids, so the caller can delete them under that
 * entry. Frames need no sweep — the ledger keys them by their staged moment,
 * which is dropped with the entry.
 */
export function resetUploadLedger(ledger: UploadLedger, inFlight: ReadonlySet<SendAttempt>): string[] {
  const carried = new Set([...inFlight].flatMap((attempt) => attempt.files.map((file) => file.id)));
  const forgotten: string[] = [];
  ledger.files.forEach((fileId, stagedId) => {
    if (carried.has(stagedId)) return;
    forgotten.push(fileId);
    ledger.files.delete(stagedId);
  });
  return forgotten;
}

/**
 * The server copies nothing will send once the page goes away — uploaded files
 * and the staged frame, minus whatever a send in flight carries — taken out of
 * the ledger so a page restored from the back/forward cache uploads them again.
 */
export function takeStrandedUploads(ledger: UploadLedger, inFlight: ReadonlySet<SendAttempt>, staged: StagedMomentState | null): string[] {
  const ids = resetUploadLedger(ledger, inFlight);
  if (!staged || momentInFlight(inFlight, staged)) return ids;
  const frame = ledger.frames.get(staged);
  if (!frame) return ids;
  ledger.frames.delete(staged);
  return [...ids, frame.fileId];
}

function isUploaded(ledger: UploadLedger, id: string): boolean {
  return ledger.files.has(id);
}

/** A file's server copy is spent (a message sent it) or gone: no later send may name it. */
function forgetFileUpload(ledger: UploadLedger, id: string): void {
  ledger.files.delete(id);
}

function forgetFileUploads(ledger: UploadLedger, files: StagedFile[]): void {
  files.forEach((file) => forgetFileUpload(ledger, file.id));
}

/** Everything an attempt carried: its files and its frame. */
export function forgetAttemptUploads(ledger: UploadLedger, attempt: SendAttempt): void {
  forgetFileUploads(ledger, attempt.files);
  if (!attempt.staged) return;
  ledger.frames.delete(attempt.staged);
}

/**
 * Takes the server copies an attempt carried out of the ledger and returns
 * their ids, for a send that failed after its session was gone — no retry will
 * ever name them.
 */
export function takeAttemptServerCopies(ledger: UploadLedger, attempt: SendAttempt): string[] {
  const ids = attempt.files.map((file) => ledger.files.get(file.id));
  if (attempt.staged) ids.push(ledger.frames.get(attempt.staged)?.fileId);
  forgetAttemptUploads(ledger, attempt);
  return ids.filter((id): id is string => id !== undefined);
}

export function hasSendContent({ staged, files }: SendContents): boolean {
  return !!staged || files.length > 0;
}

function sameFiles(sent: StagedFile[], staged: StagedFile[]): boolean {
  if (sent.length !== staged.length) return false;
  return sent.every((file, index) => file.id === staged[index]?.id);
}

/** A retry: the same text, the same moment, the same files in the same order, and the same reply target. */
export function isRetryOf(attempt: SendAttempt, contents: SendContents): boolean {
  if (attempt.body !== contents.body) return false;
  if (attempt.staged !== contents.staged) return false;
  if (attempt.replyTo !== contents.replyTo) return false;
  return sameFiles(attempt.files, contents.files);
}

function carriesFile(attempt: SendAttempt, id: string): boolean {
  return attempt.files.some((file) => file.id === id);
}

/** A send in flight shows this moment on its optimistic bubble. */
export function momentInFlight(sending: ReadonlySet<SendAttempt>, staged: StagedMomentState): boolean {
  return Array.from(sending).some((attempt) => attempt.staged === staged);
}

/** A send in flight shows this file on its optimistic bubble. */
export function fileInFlight(sending: ReadonlySet<SendAttempt>, id: string): boolean {
  return Array.from(sending).some((attempt) => carriesFile(attempt, id));
}

/**
 * The upload name for an encoded image: `stem` plus the extension its bytes
 * really have — the backend checks a file's extension against them.
 */
export function encodedImageName(stem: string, contentType: EncodedImage['contentType']): string {
  return `${stem}${ENCODED_IMAGE_EXTENSION[contentType]}`;
}

function fileStem(name: string): string {
  const dot = name.lastIndexOf(EXTENSION_SEPARATOR);
  if (dot === NO_EXTENSION) return name;
  return name.slice(0, dot);
}

/** `shot.png` compressed to WebP uploads as `shot.webp`. */
function compressedName(name: string, contentType: EncodedImage['contentType']): string {
  return encodedImageName(fileStem(name), contentType);
}

function pickedDraft(staged: StagedFile): FileDraft {
  return { file: staged.file, name: staged.name, kind: staged.kind };
}

function compressedDraft(staged: StagedFile, compressed: CompressedImage): FileDraft {
  return { file: compressed.blob, name: compressedName(staged.name, compressed.contentType), kind: staged.kind };
}

function uploadedDraft(staged: StagedFile, fileId: string): FileDraft {
  return { ...pickedDraft(staged), fileId };
}

/**
 * Compression only saves bytes, so a failure to encode must not block the
 * send: the image goes as picked. Logged, not swallowed, so a browser that
 * can't encode stays visible.
 */
async function compressOrNull(file: File, fileName: string): Promise<CompressedImage | null> {
  try {
    return await compressImageFile(file);
  } catch (err) {
    // Over the backend's size limits: the server would refuse it too, so the send says so now.
    if (err instanceof ImageTooLargeError) throw imageTooLarge(fileName, err);
    console.warn('[sendDraft] image compression failed — uploading the picked file as it is', err);
    return null;
  }
}

/** The compressed image when it's smaller; the picked one when the browser can't decode it or nothing is saved. */
function smallerImageDraft(staged: StagedFile, compressed: CompressedImage | null): FileDraft {
  if (!compressed) return pickedDraft(staged);
  if (pickSmaller(staged.file, compressed) !== compressed.blob) return pickedDraft(staged);
  return compressedDraft(staged, compressed);
}

/** The same refusal the server gives, raised before any byte uploads. */
function fileTooLarge(fileName: string): MessageSendError {
  return new MessageSendError('file exceeds the maximum allowed size', 'file_rejected', undefined, { code: FILE_TOO_LARGE_CODE, fileName });
}

function imageTooLarge(fileName: string, cause: ImageTooLargeError): MessageSendError {
  return new MessageSendError('image is over the maximum dimensions', 'file_rejected', cause, { code: IMAGE_TOO_LARGE_CODE, fileName });
}

/** The API error code an error carries, when it carries one. */
function codeOf(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null || !('code' in err)) return undefined;
  const { code } = err;
  return typeof code === 'string' ? code : undefined;
}

/**
 * A file the server refused on its own upload, as the send path would have
 * reported it: carrying the server's code and the file's name, so the same
 * copy says which file and why.
 */
export function uploadRefusal(err: unknown, fileName: string): MessageSendError {
  if (err instanceof MessageSendError) return err;
  const message = err instanceof Error ? err.message : DEFAULT_REFUSAL_MESSAGE;
  return new MessageSendError(message, 'file_rejected', err, { code: codeOf(err), fileName });
}

/**
 * An image may be picked over the per-file limit because its compressed copy
 * uploads; one that is still over it after compression is refused here.
 */
async function imageDraft(staged: StagedFile, maxFileBytes: number): Promise<FileDraft> {
  const draft = smallerImageDraft(staged, await compressOrNull(staged.file, staged.name));
  if (draft.file.size > maxFileBytes) throw fileTooLarge(staged.name);
  return draft;
}

/** A file an earlier send uploaded rides as the server's copy: never compressed or uploaded again. */
export function fileDraftFor(staged: StagedFile, ledger: UploadLedger, maxFileBytes: number): Promise<FileDraft> {
  const fileId = ledger.files.get(staged.id);
  if (fileId) return Promise.resolve(uploadedDraft(staged, fileId));
  if (staged.kind !== COMPRESSIBLE_KIND) return Promise.resolve(pickedDraft(staged));
  return imageDraft(staged, maxFileBytes);
}

/** One at a time: each image decodes to a full-size bitmap, so ten at once could hold ten in memory. */
async function toFileDrafts(files: StagedFile[], ledger: UploadLedger, maxFileBytes: number): Promise<FileDraft[]> {
  const drafts: FileDraft[] = [];
  for (const file of files) {
    drafts.push(await fileDraftFor(file, ledger, maxFileBytes));
  }
  return drafts;
}

/** The moment as this send carries it — its frame marked as the server's copy once an earlier send uploaded it. */
function momentDraft(staged: StagedMomentState | null, ledger: UploadLedger): MomentDraft | undefined {
  if (!staged) return undefined;
  const { moment } = staged;
  const uploaded = ledger.frames.get(staged);
  if (!moment.frame || !uploaded) return moment;
  return { ...moment, frame: { ...moment.frame, uploaded } };
}

async function buildDraft(attempt: SendAttempt, ledger: UploadLedger, maxFileBytes: number): Promise<MessageDraft> {
  const files = await toFileDrafts(attempt.files, ledger, maxFileBytes);
  return { moment: momentDraft(attempt.staged, ledger), files, replyTo: attempt.replyTo };
}

async function buildDraftOnce(attempt: SendAttempt, ledger: UploadLedger, maxFileBytes: number): Promise<MessageDraft> {
  try {
    return await buildDraft(attempt, ledger, maxFileBytes);
  } catch (err) {
    // Never keep a failed build: the retry builds again rather than replaying the failure.
    attempt.draft = undefined;
    throw err;
  }
}

/** The attempt's draft — the SAME object on every retry, built on first use from what the ledger holds then. */
export function draftFor(attempt: SendAttempt, ledger: UploadLedger, maxFileBytes: number): Promise<MessageDraft> {
  if (!attempt.draft) attempt.draft = buildDraftOnce(attempt, ledger, maxFileBytes);
  return attempt.draft;
}

export type StagedFileUpdate = (file: StagedFileState) => StagedFileState;

function startingProgress(ledger: UploadLedger, id: string): number {
  if (isUploaded(ledger, id)) return FULL_RING;
  return EMPTY_RING;
}

/** A send starts: each file it carries shows its ring — full for one an earlier send already uploaded. */
export function uploadStarted(attempt: SendAttempt, ledger: UploadLedger): StagedFileUpdate {
  return function startUpload(file: StagedFileState): StagedFileState {
    if (!carriesFile(attempt, file.id)) return file;
    return { ...file, failed: false, progress: startingProgress(ledger, file.id) };
  };
}

/** A progress fraction in the whole percents a chip's ring shows. */
export function toWholePercent(fraction: number): number {
  return Math.round(fraction * PROGRESS_STEPS) / PROGRESS_STEPS;
}

/** Moves one chip's ring — and leaves the file untouched (the same object) when the ring wouldn't change. */
export function uploadProgressed(id: string, fraction: number): StagedFileUpdate {
  const progress = toWholePercent(fraction);
  return function showProgress(file: StagedFileState): StagedFileState {
    if (file.id !== id || file.progress === progress) return file;
    return { ...file, progress };
  };
}

/** An eager upload ended without a refusal: the chip drops its ring — the send takes over from here. */
export function uploadSettled(id: string): StagedFileUpdate {
  return function clearRing(file: StagedFileState): StagedFileState {
    if (file.id !== id || file.progress === undefined) return file;
    return { ...file, progress: undefined };
  };
}

/** The server refused this file before any send: its chip shows failed. */
export function fileRejected(id: string): StagedFileUpdate {
  return function markRejected(file: StagedFileState): StagedFileState {
    if (file.id !== id) return file;
    return { ...file, progress: undefined, failed: true };
  };
}

/**
 * The send failed: every file the server doesn't hold — refused, even after
 * every byte went out, or cut short when another file failed — is marked
 * failed, and a retry uploads it again. Files the server holds just lose
 * their ring.
 */
export function uploadFailed(attempt: SendAttempt, ledger: UploadLedger): StagedFileUpdate {
  return function markFailure(file: StagedFileState): StagedFileState {
    if (!carriesFile(attempt, file.id)) return file;
    return { ...file, progress: undefined, failed: !isUploaded(ledger, file.id) };
  };
}

/** The send was dropped, not failed (the moment's cut is gone): its files wait for the next send, unmarked. */
export function uploadDropped(attempt: SendAttempt): StagedFileUpdate {
  return function clearProgress(file: StagedFileState): StagedFileState {
    if (!carriesFile(attempt, file.id)) return file;
    return { ...file, progress: undefined, failed: false };
  };
}

function isFrameItem(index: number): boolean {
  return index === FRAME_ITEM_INDEX;
}

function recordFrameUpload(attempt: SendAttempt, ledger: UploadLedger, uploaded: UploadedFile): void {
  if (!attempt.staged) return;
  ledger.frames.set(attempt.staged, uploaded);
}

/**
 * What the session hands the sender for one attempt: each draft item's
 * progress goes to the chip of the staged file at that position, and an item
 * goes into the ledger only once the sender says its upload came back with a
 * file id — never on bytes sent alone. The moment's frame has its own index
 * and no chip.
 */
export function sendCallbacksFor(
  attempt: SendAttempt,
  ledger: UploadLedger,
  onFileProgress: (id: string, fraction: number) => void,
): SendCallbacks {
  function reportProgress(index: number, fraction: number): void {
    const file = attempt.files[index];
    if (!file) return;
    onFileProgress(file.id, fraction);
  }
  function recordUploaded(index: number, uploaded: UploadedFile): void {
    if (isFrameItem(index)) {
      recordFrameUpload(attempt, ledger, uploaded);
      return;
    }
    const file = attempt.files[index];
    if (!file) return;
    ledger.files.set(file.id, uploaded.fileId);
  }
  return { onProgress: reportProgress, onUploaded: recordUploaded };
}
